import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, RotateCcw, Award, Sparkles } from "lucide-react";
import apiClient from "../lib/api";
import { useI18n } from "../i18n";
import ProductCard from "../components/store/ProductCard";
import Marquee from "../components/store/Marquee";
import TestimonialsCarousel from "../components/store/TestimonialsCarousel";
import { Reveal } from "../components/store/Reveal";
import { toast } from "sonner";

const NewsletterInline = () => {
  const { lang } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await apiClient.post("/newsletter", { email, language: lang });
      if (data?.discount_code) {
        setCode(data.discount_code);
        toast.success(lang === "nl" ? `Code: ${data.discount_code}` : `Code: ${data.discount_code}`);
      } else {
        toast.success(lang === "nl" ? "Je bent al ingeschreven 🎉" : "You're already subscribed 🎉");
      }
      setEmail("");
    } catch {
      toast.error("Error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-8 max-w-md mx-auto">
      <form onSubmit={submit} className="flex border border-white/30">
        <input required type="email" data-testid="home-newsletter-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={lang === "nl" ? "Je e-mailadres" : "Your email address"} className="flex-1 bg-transparent text-white text-sm px-4 py-3.5 outline-none placeholder-white/40" />
        <button disabled={busy} data-testid="home-newsletter-submit" className="bg-terracotta hover:bg-[#b85f47] text-white px-6 text-sm font-medium transition-colors">{lang === "nl" ? "Inschrijven" : "Subscribe"}</button>
      </form>
      {code && (
        <div className="mt-4 inline-block border border-terracotta text-terracotta font-label px-4 py-2" data-testid="newsletter-code-shown">
          {lang === "nl" ? "Jouw code:" : "Your code:"} <strong className="font-display text-base ml-2">{code}</strong>
        </div>
      )}
      <p className="text-xs text-white/40 mt-4">{lang === "nl" ? "Door je in te schrijven ga je akkoord met onze privacy­voorwaarden." : "By subscribing you agree to our privacy policy."}</p>
    </div>
  );
};

const CATEGORY_IMAGES = {
  "home-living": "https://images.unsplash.com/photo-1567016376408-0226e4d0c1ea?w=1200&auto=format&fit=crop",
  "smart-gadgets": "https://images.unsplash.com/photo-1650682009477-52fd77302b78?w=1200&auto=format&fit=crop",
  "pet-products": "https://images.unsplash.com/photo-1753826944253-76cd9e60cd04?w=1200&auto=format&fit=crop",
  "kitchen-accessories": "https://images.unsplash.com/photo-1779387733080-8d8654b24802?w=1200&auto=format&fit=crop",
  "car-accessories": "https://images.unsplash.com/photo-1533630217389-3a5e4dff5683?w=1200&auto=format&fit=crop",
};

const Home = () => {
  const { t, lang } = useI18n();
  const [featured, setFeatured] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cms, setCms] = useState(null);

  useEffect(() => {
    (async () => {
      const [f, b, n, c, s] = await Promise.all([
        apiClient.get("/products", { params: { featured: true, limit: 8 } }),
        apiClient.get("/products", { params: { best_seller: true, limit: 4 } }),
        apiClient.get("/products", { params: { new_arrival: true, limit: 4 } }),
        apiClient.get("/categories"),
        apiClient.get("/settings"),
      ]);
      setFeatured(f.data.items);
      setBestsellers(b.data.items);
      setNewArrivals(n.data.items);
      setCategories(c.data);
      setCms(s.data);
    })();
  }, []);

  const hp = cms?.homepage || {};
  const heroTitle = lang === "nl" ? (hp.hero_title_nl || t("home_hero_title")) : (hp.hero_title_en || t("home_hero_title"));
  const heroSubtitle = lang === "nl" ? (hp.hero_subtitle_nl || t("home_hero_sub")) : (hp.hero_subtitle_en || t("home_hero_sub"));
  const heroCta = lang === "nl" ? (hp.hero_cta_label_nl || t("home_hero_cta")) : (hp.hero_cta_label_en || t("home_hero_cta"));
  const heroImage = hp.hero_image || "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?w=1800&auto=format&fit=crop";
  const marqueeItems = hp.marquee_messages && hp.marquee_messages.length > 0 ? hp.marquee_messages : [
    t("free_ship_nl"), `★ ${t("rating")}`, "iDEAL · Klarna · Apple Pay", t("returns"), "PostNL & DHL", "Made for NL",
  ];

  const faqs = lang === "nl" ? [
    { q: "Hoe snel wordt mijn bestelling geleverd?", a: "PostNL standaard 2-3 werkdagen, express volgende werkdag. Gratis verzending vanaf €50." },
    { q: "Welke betaalmethoden zijn beschikbaar?", a: "iDEAL, creditcard, PayPal, Apple Pay, Google Pay en Klarna achteraf betalen." },
    { q: "Kan ik retourneren?", a: "Ja, binnen 30 dagen zonder vragen. Wij betalen het retourlabel bij artikelen boven €50." },
    { q: "Leveren jullie ook in België en Duitsland?", a: "Momenteel sturen we binnen Nederland. België en Duitsland volgen binnenkort." },
  ] : [
    { q: "How fast will my order arrive?", a: "PostNL standard 2-3 business days, express next day. Free shipping over €50." },
    { q: "Which payment methods are available?", a: "iDEAL, credit card, PayPal, Apple Pay, Google Pay and Klarna pay-later." },
    { q: "Can I return items?", a: "Yes, within 30 days, no questions asked. We cover return labels above €50." },
    { q: "Do you ship to Belgium and Germany?", a: "We currently ship within the Netherlands. Belgium and Germany are coming soon." },
  ];

  const whyUs = [
    { icon: Truck, title: lang === "nl" ? "Voor 23:00, morgen in huis" : "Order by 23:00, delivered tomorrow", body: lang === "nl" ? "PostNL & DHL — gratis vanaf €50" : "PostNL & DHL — free over €50" },
    { icon: ShieldCheck, title: lang === "nl" ? "Veilig betalen" : "Secure payments", body: lang === "nl" ? "iDEAL, Klarna, kaart, Apple/Google Pay" : "iDEAL, Klarna, card, Apple/Google Pay" },
    { icon: RotateCcw, title: lang === "nl" ? "30 dagen retour" : "30-day returns", body: lang === "nl" ? "Niet goed? Geld terug." : "Not right? Money back." },
    { icon: Award, title: lang === "nl" ? "Beoordeeld 4.9 ★" : "Rated 4.9 ★", body: lang === "nl" ? "Door 12.000+ klanten" : "By 12,000+ customers" },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO — Editorial split */}
      <section className="relative overflow-hidden border-b border-black/5">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-10 md:pt-16 pb-20 md:pb-28 grid md:grid-cols-12 gap-8 md:gap-12 items-end">
          <div className="md:col-span-5 lg:col-span-5 relative z-10">
            <Reveal>
              <div className="font-label text-terracotta mb-6 flex items-center gap-2">
                <span className="w-6 h-px bg-terracotta inline-block" />
                {t("home_hero_eyebrow")}
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="font-display text-[14vw] sm:text-[10vw] md:text-[6vw] lg:text-[5.5vw] xl:text-[5vw] font-light tracking-tighter leading-[0.92]">
                {heroTitle}
              </h1>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-7 text-base md:text-lg text-ink-muted leading-relaxed max-w-md">{heroSubtitle}</p>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-8 flex gap-3 flex-wrap">
                <Link to="/shop" data-testid="hero-cta-primary" className="btn-primary text-sm">{heroCta} <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/faq" data-testid="hero-cta-secondary" className="btn-secondary text-sm">{t("home_hero_secondary")}</Link>
              </div>
            </Reveal>
            <Reveal delay={400}>
              <div className="mt-12 flex items-center gap-8 text-xs text-ink-muted">
                <div><div className="font-display text-3xl text-ink">4.9</div><div className="font-label mt-1">/5 rating</div></div>
                <div className="w-px h-10 bg-black/10" />
                <div><div className="font-display text-3xl text-ink">12k+</div><div className="font-label mt-1">{lang === "nl" ? "klanten" : "customers"}</div></div>
                <div className="w-px h-10 bg-black/10" />
                <div><div className="font-display text-3xl text-ink">30d</div><div className="font-label mt-1">{t("returns")}</div></div>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-7 lg:col-span-7 relative">
            <div className="editorial-image relative">
              <img alt="Hero" src={heroImage} className="w-full h-[70vh] md:h-[82vh] object-cover" />
              <div className="absolute bottom-6 left-6 right-6 md:bottom-8 md:left-8 md:right-8 flex justify-between items-end text-white">
                <div>
                  <div className="font-label opacity-80">Editorial 01 · Winter 2026</div>
                  <div className="font-display text-2xl md:text-3xl mt-2">Quiet living, considered objects</div>
                </div>
                <Link to="/shop?category=home-living" className="font-label underline underline-offset-4 hover:text-terracotta">{lang === "nl" ? "Wonen" : "Home & Living"} →</Link>
              </div>
            </div>
            {/* Floating product badge */}
            <Reveal delay={500} className="absolute -bottom-8 -left-4 md:-left-12 hidden md:block">
              <div className="bg-white border border-black/10 p-5 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.25)] max-w-[260px]">
                <div className="font-label text-terracotta mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Editor's pick</div>
                <div className="font-display text-lg leading-tight">{lang === "nl" ? "Keramische Tafellamp" : "Ceramic Table Lamp"}</div>
                <div className="font-display mt-1">€129.00</div>
                <Link to="/product/ceramic-table-lamp" className="block mt-3 text-xs font-label underline underline-offset-4">{lang === "nl" ? "Bekijk" : "View"} →</Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee items={marqueeItems} />

      {/* WHY US */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {whyUs.map(({ icon: Icon, title, body }, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="border border-black/10 bg-white p-5 md:p-7 h-full hover:border-ink transition-colors group">
                <Icon className="w-6 h-6 text-terracotta mb-4 group-hover:scale-110 transition-transform" />
                <div className="font-display text-lg leading-tight">{title}</div>
                <div className="text-xs text-ink-muted mt-1.5">{body}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORIES — asymmetric bento */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-20">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="font-label text-ink-muted">02</div>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-light mt-2">{t("nav_categories")}</h2>
            </div>
            <Link to="/shop" className="font-label hover:text-terracotta">All →</Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4">
          {categories.map((c, i) => (
            <Link
              key={c.slug}
              to={`/shop?category=${c.slug}`}
              data-testid={`category-tile-${c.slug}`}
              className={`editorial-image relative aspect-square md:aspect-auto ${i === 0 ? "md:col-span-3 md:row-span-2 md:aspect-[3/2]" : "md:col-span-3 md:aspect-[2/1]"} ${i === 1 ? "md:col-span-3" : ""} ${i === 2 || i === 3 ? "md:col-span-2 md:aspect-square" : ""} ${i === 4 ? "md:col-span-2 md:aspect-square" : ""} group overflow-hidden`}
            >
              <img src={CATEGORY_IMAGES[c.slug]} alt={c.name_en} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-transparent group-hover:from-black/60 transition-all" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="font-display text-xl md:text-3xl drop-shadow">{lang === "nl" ? c.name_nl : c.name_en}</div>
                <div className="font-label mt-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all">{t("nav_shop")} →</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-20">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="font-label text-ink-muted">03</div>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-light mt-2">{t("home_featured")}</h2>
            </div>
            <Link to="/shop" className="font-label hover:text-terracotta">{t("nav_shop")} →</Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
          {featured.slice(0, 8).map((p, i) => (
            <Reveal key={p.product_id} delay={(i % 4) * 80}><ProductCard product={p} /></Reveal>
          ))}
        </div>
      </section>

      {/* EDITORIAL CALLOUT */}
      <section className="bg-ink text-white py-20 md:py-28 relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <Reveal><div className="font-label text-terracotta">{lang === "nl" ? "Onze filosofie" : "Our philosophy"}</div></Reveal>
            <Reveal delay={120}>
              <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-light mt-3 leading-[0.95]">
                {lang === "nl" ? <>Minder spullen,<br /><em className="not-italic text-terracotta">betere</em> spullen.</> : <>Less stuff,<br /><em className="not-italic text-terracotta">better</em> stuff.</>}
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-6 text-white/70 max-w-md leading-relaxed">
                {lang === "nl" ? "We werken samen met Europese makers en kiezen objecten die jaren meegaan. Geen seizoenen, geen ruis." : "We work with European makers and choose objects that last for years. No seasons, no noise."}
              </p>
            </Reveal>
            <Reveal delay={300}>
              <Link to="/shop" className="inline-flex items-center gap-2 mt-8 text-sm font-label underline underline-offset-4 hover:text-terracotta">{lang === "nl" ? "Onze collectie" : "Our collection"} <ArrowRight className="w-4 h-4" /></Link>
            </Reveal>
          </div>
          <div className="md:col-span-6 grid grid-cols-2 gap-3">
            <div className="editorial-image aspect-[3/4]"><img src="https://images.unsplash.com/photo-1485808191679-5f86510681a2?w=800&auto=format&fit=crop" alt="" className="w-full h-full object-cover" /></div>
            <div className="editorial-image aspect-[3/4] mt-12"><img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&auto=format&fit=crop" alt="" className="w-full h-full object-cover" /></div>
          </div>
        </div>
      </section>

      {/* BESTSELLERS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="font-label text-ink-muted">04</div>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-light mt-2">{t("home_bestsellers")}</h2>
            </div>
            <Link to="/shop?sort=rating" className="font-label hover:text-terracotta">All →</Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
          {bestsellers.map((p, i) => <Reveal key={p.product_id} delay={i * 80}><ProductCard product={p} /></Reveal>)}
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="bg-secondary-warm py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="font-label text-ink-muted">05</div>
                <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-light mt-2">{t("home_new")}</h2>
              </div>
              <Link to="/shop" className="font-label hover:text-terracotta">{t("nav_shop")} →</Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
            {newArrivals.map((p, i) => <Reveal key={p.product_id} delay={i * 80}><ProductCard product={p} /></Reveal>)}
          </div>
        </div>
      </section>

      {/* NEWSLETTER 10% INCENTIVE */}
      <section className="relative bg-ink text-white py-20 overflow-hidden" data-testid="newsletter-section">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <img src="https://images.unsplash.com/photo-1522444690501-83bd1c3c0f5d?w=1600&auto=format&fit=crop" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 border border-terracotta text-terracotta font-label px-3 py-1.5 mb-6">
            <Sparkles className="w-3.5 h-3.5" /> {lang === "nl" ? "10% KORTING — ALLEEN VOOR INSCHRIJVERS" : "10% OFF — SUBSCRIBERS ONLY"}
          </div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-light leading-[0.95]">
            {lang === "nl" ? <>Word lid van <em className="not-italic text-terracotta">SnapShop</em>.</> : <>Join <em className="not-italic text-terracotta">SnapShop</em>.</>}
          </h2>
          <p className="mt-5 text-white/70 max-w-md mx-auto">{lang === "nl" ? "Schrijf je in en ontvang direct 10% korting op je eerste bestelling. Eén e-mail per maand. Nooit spam." : "Subscribe and get 10% off your first order — instantly. One email per month. Never spam."}</p>
          <NewsletterInline />
        </div>
      </section>

      {/* TESTIMONIALS CAROUSEL */}
      <TestimonialsCarousel />

      {/* FAQ */}
      <section className="bg-ink text-white py-20">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-12">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-light">{t("home_faq")}</h2>
            <p className="mt-4 text-white/60 text-sm">Need help? <Link to="/faq" className="underline">More questions →</Link></p>
          </Reveal>
          <div className="md:col-span-2 divide-y divide-white/10">
            {faqs.map((f, i) => (
              <details key={i} className="py-5 group" data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer flex justify-between items-center font-display text-lg gap-4">
                  {f.q}
                  <span className="text-terracotta text-2xl transition-transform group-open:rotate-45 shrink-0">+</span>
                </summary>
                <p className="text-white/70 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
